import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Car, Cpu, Compass, Sparkles, Users } from "lucide-react";

const CATEGORY_CARDS = [
  { icon: <Building2 size={22} />, title: "Real Estate", copy: "Connect with homebuyers exploring the same projects or locations." },
  { icon: <Car size={22} />, title: "Automobile", copy: "Find buyers planning to purchase the same car or bike and negotiate together." },
  { icon: <Cpu size={22} />, title: "Electronics", copy: "Join groups interested in the latest smartphones, laptops, appliances, and other consumer electronics." },
];

const VALUES = [
  { icon: <Users size={18} />, label: "Buyers unite" },
  { icon: <Sparkles size={18} />, label: "Real insights" },
  { icon: <Compass size={18} />, label: "Transparent journey" },
];

export default function AboutUsPage() {
  return (
    <div className="bg-transparent" data-testid="about-us-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="bhega-pill pill-glow mb-6 w-fit" data-testid="about-eyebrow">
            <Sparkles size={12} /> About BHEGA
          </div>
          <h1 className="display-1 text-foreground" data-testid="about-hero-title">
            Smarter buying<br/>
            <span className="text-brand text-glow-yellow">starts together.</span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-light">
            BHEGA is a community-driven platform that brings together people who are planning to purchase the same product around the same time. Instead of negotiating alone, buyers can connect, exchange information, compare options, and leverage their collective buying power to secure better deals.
          </p>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl leading-relaxed font-light">
            Whether you're looking for your next home, vehicle, or electronic gadget, BHEGA helps you discover others with similar purchase intentions, making the buying journey more informed, transparent, and rewarding.
          </p>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="about-value-pills">
            {VALUES.map((v) => (
              <span key={v.label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/5 border border-border text-xs font-semibold text-foreground">
                <span className="text-brand">{v.icon}</span> {v.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-b border-border">
        <div className="eyebrow mb-3">01 · Our focus</div>
        <h2 className="display-2 text-foreground" data-testid="about-categories-title">
          Three categories.<br/>
          <span className="text-brand">One collective power.</span>
        </h2>
        <p className="mt-5 text-muted-foreground text-lg max-w-2xl leading-relaxed">
          Our platform currently focuses on three major categories where buyers benefit most from negotiating together.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-5 stagger">
          {CATEGORY_CARDS.map((c) => (
            <article
              key={c.title}
              className="bhega-card p-7"
              data-testid={`about-category-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-[#FFBE06]/15 border border-[#FFBE06]/30 text-brand rounded-full">
                {c.icon}
              </div>
              <h3 className="font-heading font-extrabold text-2xl mt-5 text-foreground tracking-tight">{c.title}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{c.copy}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Journey */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-b border-border">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:items-start">
          <div className="lg:sticky lg:top-24">
            <div className="eyebrow mb-3">02 · Our Journey</div>
            <h2 className="display-2 text-foreground">Built by <span className="text-brand">CompuBrain.</span></h2>
          </div>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed" data-testid="about-journey-body">
            <p>
              BHEGA was envisioned and developed by <strong className="text-foreground font-bold">CompuBrain</strong> as part of its mission to create technology that solves real-world challenges through innovation. With years of experience in building digital platforms and business solutions, CompuBrain identified an opportunity to transform the way people make major purchase decisions.
            </p>
            <p>
              This vision led to the creation of BHEGA — a collaborative buying platform that connects genuine buyers, enabling them to share insights, coordinate purchases, and negotiate better deals together. Today, BHEGA continues to evolve as one of CompuBrain's flagship digital initiatives, focused on making collective buying simple, transparent, and rewarding.
            </p>
          </div>
        </div>
      </section>

      {/* Belief pull-quote */}
      <section className="relative overflow-hidden border-b border-border py-24">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow mb-3">03 · What we believe</div>
          <blockquote className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.05] max-w-4xl" data-testid="about-belief-quote">
            &ldquo;Buyers make <span className="text-brand text-glow-yellow">better decisions</span> together.&rdquo;
          </blockquote>
          <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            At BHEGA, we believe buyers make better decisions together. By bringing people with similar buying intentions onto a single platform, we enable informed discussions, stronger negotiation, and greater value for every purchase.
          </p>
        </div>
      </section>

      {/* Looking Ahead + CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:items-center">
          <div>
            <div className="eyebrow mb-3">04 · Looking ahead</div>
            <h2 className="display-2 text-foreground">The future of<br/><span className="text-brand">collective buying.</span></h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              As BHEGA grows across Real Estate, Automobiles, and Electronics, it remains backed by the innovation and technology expertise of CompuBrain. Together, we are building a trusted platform that empowers consumers to buy smarter through the power of collective purchasing.
            </p>
          </div>

          <div className="bhega-card p-8" data-testid="about-cta-card">
            <div className="eyebrow text-brand">Get started</div>
            <h3 className="font-heading font-extrabold text-3xl mt-2 leading-tight text-foreground">Ready to Bhega?</h3>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">Explore live listings across our three categories and join buyers negotiating right now.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/browse" className="btn-primary" data-testid="about-cta-browse">
                Explore deals <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" className="btn-secondary" data-testid="about-cta-how">
                How it works
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
